import React from 'react';
import {
	addBreadcrumb,
	type ErrorEvent,
	replayIntegration,
	setContext,
	setTag,
	setUser,
} from '@sentry/browser';
import { init, reactRouterV6BrowserTracingIntegration } from '@sentry/react';
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from 'react-router-dom';

import { Env, Integrations, Urls } from 'src/appConstants';
import useUserStore from 'src/hooks/stores/useUserStore';
import {
	sanitizeBreadcrumbData,
	sanitizePayloadForSentry,
} from 'src/modules/sentry-sanitize';
import {
	getSentryRouteContext,
	normalizeApiRoute,
} from 'src/modules/sentry-taxonomy';
import { installSentryTestScenarios } from 'src/modules/sentry-test-scenarios';
import {
	applyChunkErrorContext,
	isChunkErrorEvent,
	shouldDropChunkError,
} from 'src/modules/sentry-chunks';

const sentryRelease = process.env.REACT_APP_SENTRY_RELEASE;

type SentryEvent = {
	message?: string;
	request?: { url?: string };
	extra?: {
		__serialized__?: {
			status?: number;
			statusText?: string;
			data?: {
				code?: string;
				message?: string;
				status?: number;
			};
			config?: {
				url?: string;
				routeTemplate?: string;
				method?: string;
			};
		};
		[key: string]: unknown;
	};
	user?: {
		id?: string | number;
		email?: string;
		username?: string;
	};
	tags?: Record<string, string>;
	fingerprint?: string[];
	exception?: {
		values?: Array<{
			type?: string;
			value?: string;
			stacktrace?: {
				frames?: Array<{
					filename?: string;
					abs_path?: string;
				}>;
			};
		}>;
	};
};

type SentryBreadcrumb = {
	category?: string;
	level?: string;
	message?: string;
	data?: Record<string, any>;
};

const externalNoisePatterns = [
	/iabjs:\/\//i,
	/autofill_contact_enhanced/i,
	/navigation_performance_logger_android/i,
	/java object is gone/i,
	/window\.webkit\.messageHandlers/i,
	/enableDidUserTypeOnKeyboardLogging/i,
	/enableButtonsClickedMetaDataLogging/i,
	/snaptube/i,
	/ResizeObserver loop limit exceeded/i,
	/ResizeObserver loop completed with undelivered notifications/i,
];

const replayNetworkAllowList = [
	Urls.API,
	Urls.AUTH_API,
	Urls.BFF_API,
	Urls.EVENT_API,
	Urls.CHECKOUT_API,
	Urls.PAGARME_API,
].filter((url): url is string => Boolean(url));

let hasInitializedSentry = false;
const emailLookupHashCache = new Map<string, Promise<string | null>>();

const SENTRY_CLIENT_ERROR_LIMIT = 5;
const SENTRY_CLIENT_ERROR_WINDOW_MS = 10_000;

let clientErrorCount = 0;
let lastClientErrorReset = Date.now();

const getSentryDsnHost = () => {
	if (!Integrations.SENTRY_DSN) return null;

	try {
		return new URL(Integrations.SENTRY_DSN).hostname;
	} catch {
		return null;
	}
};

const sentryDsnHost = getSentryDsnHost();

const normalizeLookupEmail = (email: string) => email.trim().toLowerCase();

const getSearchKeys = (search: string) => {
	try {
		return [...new URLSearchParams(search).keys()].sort();
	} catch {
		return [];
	}
};

const sanitizeUrlForObservability = (url?: string) => {
	if (!url) return undefined;

	try {
		const baseUrl =
			typeof window !== 'undefined'
				? window.location.origin
				: 'https://uticket.local';
		const parsedUrl = new URL(url, baseUrl);
		const searchKeys = [...parsedUrl.searchParams.keys()].sort();
		const sanitizedPath = `${parsedUrl.pathname}${
			searchKeys.length ? `?${searchKeys.join('&')}` : ''
		}`;

		if (/^https?:\/\//i.test(url)) {
			return `${parsedUrl.origin}${sanitizedPath}`;
		}

		return sanitizedPath;
	} catch {
		return url.split('?')[0];
	}
};

const getEmailLookupHash = (email: string) => {
	const normalizedEmail = normalizeLookupEmail(email);

	if (emailLookupHashCache.has(normalizedEmail)) {
		return emailLookupHashCache.get(normalizedEmail)!;
	}

	const hashPromise = (async () => {
		if (!globalThis.crypto?.subtle) {
			return null;
		}

		const digest = await globalThis.crypto.subtle.digest(
			'SHA-256',
			new TextEncoder().encode(normalizedEmail)
		);

		return Array.from(new Uint8Array(digest))
			.map(byte => byte.toString(16).padStart(2, '0'))
			.join('')
			.slice(0, 32);
	})().catch(() => null);

	emailLookupHashCache.set(normalizedEmail, hashPromise);

	return hashPromise;
};

const getProductionTraceSampleRate = (pathname: string) => {
	const routeContext = getSentryRouteContext(pathname);

	if (routeContext.area === 'checkout') {
		return 0.5;
	}

	if (routeContext.area === 'auth') {
		return 0.25;
	}

	if (routeContext.area === 'event_page') {
		return 0.15;
	}

	if (routeContext.area === 'panel') {
		return 0.1;
	}

	if (routeContext.area === 'home') {
		return 0.05;
	}

	return 0.03;
};

const getEventPayloadText = (event: SentryEvent) => {
	const exceptionValues = event.exception?.values ?? [];
	const exceptionText = exceptionValues
		.map(exception => `${exception.type ?? ''} ${exception.value ?? ''}`.trim())
		.join(' ');
	const framesText = exceptionValues
		.flatMap(exception => exception.stacktrace?.frames ?? [])
		.map(frame => frame.filename ?? frame.abs_path ?? '')
		.join(' ');

	return `${event.message ?? ''} ${
		event.request?.url ?? ''
	} ${exceptionText} ${framesText}`.trim();
};

const containsSentryIngestTarget = (value?: string) => {
	if (!value) return false;

	if (sentryDsnHost && value.includes(sentryDsnHost)) {
		return true;
	}

	return /(^|[./])ingest\.sentry\.io(?=[:/\s]|$)/i.test(value);
};

const isSentryTransportFailureEvent = (event: SentryEvent) => {
	if (containsSentryIngestTarget(event.request?.url)) {
		return true;
	}

	return containsSentryIngestTarget(getEventPayloadText(event));
};

const isExternalNoiseEvent = (event: SentryEvent) => {
	const payload = getEventPayloadText(event);

	return externalNoisePatterns.some(pattern => pattern.test(payload));
};

const isExpectedAuthCancellationEvent = (event: SentryEvent): boolean => {
	const values = event.exception?.values ?? [];

	return values.some(
		ex =>
			ex.type === 'AuthFlowCancelledError' ||
			(ex.value?.includes('Auth flow cancelled') ?? false)
	);
};

const isExpectedAxiosCancellationEvent = (event: SentryEvent): boolean => {
	const values = event.exception?.values ?? [];
	const cancellationMessage = /^(canceled|cancelled|request canceled|request cancelled)$/i;

	return values.some(
		exception =>
			exception.type === 'CanceledError' ||
			exception.type === 'Cancel' ||
			cancellationMessage.test(exception.value?.trim() ?? '')
	);
};

const applyRouteContextToEvent = (event: SentryEvent) => {
	const pathname = window.location.pathname;
	const routeContext = getSentryRouteContext(pathname);

	event.tags = {
		area: routeContext.area,
		flow: routeContext.flow,
		step: routeContext.step,
		route_template: routeContext.routeTemplate,
		...event.tags,
	};

	event.extra = {
		...event.extra,
		route: sanitizePayloadForSentry({
			...(event.extra?.route as Record<string, unknown> | undefined),
			ids: routeContext.ids,
		}) as any,
	};

	return event;
};

const sanitizeEventRequest = (event: SentryEvent) => {
	if (!event.request?.url) {
		return event;
	}

	event.request.url = sanitizeUrlForObservability(event.request.url);

	return event;
};

const sanitizeSerializedApiError = (event: SentryEvent) => {
	const serialized = event.extra?.__serialized__;
	if (!serialized) {
		return event;
	}

	const status = serialized.status ?? serialized.data?.status;
	const errorCode = serialized.data?.code;
	const sanitizedUrl = sanitizeUrlForObservability(serialized.config?.url);
	const routeTemplate = normalizeApiRoute(sanitizedUrl);
	const method = serialized.config?.method?.toUpperCase();

	event.tags = {
		...event.tags,
		...(method ? { api_method: method } : {}),
		api_route_template: routeTemplate,
		...(status ? { http_status: String(status) } : {}),
		...(errorCode ? { api_error_code: errorCode } : {}),
	};

	event.extra = {
		...event.extra,
		__serialized__: {
			status,
			statusText: serialized.statusText,
			data: sanitizePayloadForSentry({
				code: errorCode,
				status,
				message: serialized.data?.message,
			}) as {
				code?: string;
				message?: string;
				status?: number;
			},
			config: {
				url: sanitizedUrl,
				routeTemplate,
				method,
			},
		},
	};

	return event;
};

const beforeSentryBreadcrumb = (breadcrumb: SentryBreadcrumb) => {
	if (breadcrumb.data?.url) {
		breadcrumb.data = {
			...breadcrumb.data,
			url: sanitizeUrlForObservability(String(breadcrumb.data.url)),
		};
	}

	if (
		typeof breadcrumb.message === 'string' &&
		/^https?:\/\//i.test(breadcrumb.message)
	) {
		breadcrumb.message = sanitizeUrlForObservability(breadcrumb.message);
	}

	if (breadcrumb.data) {
		breadcrumb.data = sanitizeBreadcrumbData(breadcrumb.data);
	}

	return breadcrumb;
};

const getReplaySessionSampleRate = () => {
	if (Env === 'development') {
		return 0;
	}

	if (Env === 'staging') {
		return 0.15;
	}

	return 0.02;
};

const getReplayOnErrorSampleRate = () => (Env === 'development' ? 0 : 1.0);

export const initSentry = () => {
	installSentryTestScenarios();

	const shouldEnableSentry =
		process.env.REACT_APP_SENTRY_ENABLED === 'true' &&
		Boolean(Integrations.SENTRY_DSN);

	if (hasInitializedSentry || !shouldEnableSentry) {
		return;
	}

	hasInitializedSentry = true;

	try {
		init({
			dsn: Integrations.SENTRY_DSN,
			environment: Env,
			release: sentryRelease,
			sendDefaultPii: false,
			maxBreadcrumbs: 150,
			maxValueLength: 8_192,
			normalizeDepth: 8,
			integrations: [
				reactRouterV6BrowserTracingIntegration({
					useEffect: React.useEffect,
					useLocation,
					useNavigationType,
					createRoutesFromChildren,
					matchRoutes,
				}),
				replayIntegration({
					maskAllText: false,
					blockAllMedia: false,
					maskAllInputs: true,
					networkDetailAllowUrls: replayNetworkAllowList,
					networkCaptureBodies: false,
					beforeErrorSampling: (event: ErrorEvent) =>
						!isExternalNoiseEvent(event as SentryEvent) &&
						!isChunkErrorEvent(event),
				}),
			],
			tracesSampler: (samplingContext: any) => {
				if (Env === 'development') {
					return 1.0;
				}

				const pathname =
					typeof window !== 'undefined' ? window.location.pathname : '/';

				if (Env === 'staging') {
					return samplingContext.inheritOrSampleWith(0.2);
				}

				return samplingContext.inheritOrSampleWith(
					getProductionTraceSampleRate(pathname)
				);
			},
			replaysSessionSampleRate: getReplaySessionSampleRate(),
			replaysOnErrorSampleRate: getReplayOnErrorSampleRate(),
			ignoreErrors: externalNoisePatterns,
			beforeBreadcrumb: beforeSentryBreadcrumb,
			beforeSend: (event: SentryEvent) => {
				if (isSentryTransportFailureEvent(event)) {
					return null;
				}

				if (isExternalNoiseEvent(event)) {
					return null;
				}

				if (isExpectedAuthCancellationEvent(event)) {
					return null;
				}

				if (isExpectedAxiosCancellationEvent(event)) {
					return null;
				}

				if (shouldDropChunkError(event)) return null;

				const now = Date.now();
				if (now - lastClientErrorReset > SENTRY_CLIENT_ERROR_WINDOW_MS) {
					clientErrorCount = 0;
					lastClientErrorReset = now;
				}

				clientErrorCount++;
				if (clientErrorCount > SENTRY_CLIENT_ERROR_LIMIT) {
					return null;
				}

				const processedEvent = sanitizeSerializedApiError(
					sanitizeEventRequest(
						applyRouteContextToEvent(event)
					)
				);
				return isChunkErrorEvent(processedEvent)
					? applyChunkErrorContext(processedEvent)
					: processedEvent;
			},
		} as any);
	} catch (error) {
		hasInitializedSentry = false;

		if (process.env.NODE_ENV === 'development') {
			console.warn('Failed to initialize Sentry:', error);
		}
	}
};

const SentryScopeSync = () => {
	const location = useLocation();
	const { id, email, name, userStatus, signupSource } = useUserStore();

	React.useEffect(() => {
		if (id || email) {
			setUser({
				id: id ? String(id) : undefined,
				email: email ? normalizeLookupEmail(email) : undefined,
				username: name || undefined,
			});
		} else {
			setUser(null);
		}

		const routeContext = getSentryRouteContext(location.pathname);

		setTag('area', routeContext.area);
		setTag('flow', routeContext.flow);
		setTag('step', routeContext.step);
		setTag('route_template', routeContext.routeTemplate);

		setContext('auth', {
			userStatus: userStatus || 'anonymous',
			signupSource: signupSource || 'unknown',
		});

		setContext('route', {
			pathname: location.pathname,
			searchKeys: getSearchKeys(location.search),
			hash: location.hash,
			ids: routeContext.ids,
		});

		addBreadcrumb({
			category: 'navigation',
			type: 'navigation',
			level: 'info',
			message: location.pathname,
			data: {
				pathname: location.pathname,
				searchKeys: getSearchKeys(location.search),
				hash: location.hash,
				area: routeContext.area,
				flow: routeContext.flow,
				step: routeContext.step,
				routeTemplate: routeContext.routeTemplate,
			},
		});
	}, [
		email,
		id,
		location.hash,
		location.pathname,
		location.search,
		name,
		signupSource,
		userStatus,
	]);

	React.useEffect(() => {
		let isMounted = true;

		if (!email) {
			setTag('user_lookup_hash', 'anonymous');
			setContext('user_lookup', null);
			return () => {
				isMounted = false;
			};
		}

		getEmailLookupHash(email).then(hash => {
			if (!isMounted || !hash) {
				return;
			}

			setTag('user_lookup_hash', hash);
			setContext('user_lookup', {
				hash,
				hasEmail: true,
			});
		});

		return () => {
			isMounted = false;
		};
	}, [email]);

	return null;
};

const SentryProvider: React.FC = ({ children }) => {
	return (
		<>
			<SentryScopeSync />
			{children}
		</>
	);
};

export default SentryProvider;
